import { useCallback, useMemo, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent, TouchEvent as ReactTouchEvent } from 'react';

export type MuiTableColumnWidthDefaults = Record<string, number>;

export function sumVisibleColumnWidthsPx(visibleColumnIds: string[], getWidth: (id: string) => number): number {
  return visibleColumnIds.reduce((sum, id) => sum + getWidth(id), 0);
}

/** Sticky `left` for a column in `frozenOrder` (columns must appear in document order). */
export function stickyFrozenLeftPx(
  frozenOrder: readonly string[],
  columnId: string,
  getWidth: (id: string) => number
): number {
  const idx = frozenOrder.indexOf(columnId);
  if (idx <= 0) return 0;
  let sum = 0;
  for (let i = 0; i < idx; i++) {
    sum += getWidth(frozenOrder[i]!);
  }
  return sum;
}

/**
 * Pixel column widths for MUI `<Table layout="fixed">` + optional drag resize (matches TanStackDataTable UX).
 */
export function useMuiTableColumnResize(
  defaultWidths: MuiTableColumnWidthDefaults,
  options?: { minWidth?: number; maxWidth?: number }
) {
  const minW = options?.minWidth ?? 72;
  const maxW = options?.maxWidth ?? 960;
  const [overrides, setOverrides] = useState<Record<string, number>>({});
  const [resizingColumnId, setResizingColumnId] = useState<string | null>(null);
  const dragRef = useRef<{ id: string; startX: number; startW: number } | null>(null);

  const getWidth = useCallback(
    (columnId: string) => {
      const o = overrides[columnId];
      if (o != null) return o;
      const d = defaultWidths[columnId];
      return d != null ? d : 120;
    },
    [overrides, defaultWidths]
  );

  const colWidthSx = useCallback(
    (columnId: string) => ({
      width: getWidth(columnId),
      minWidth: getWidth(columnId),
      maxWidth: getWidth(columnId),
      boxSizing: 'border-box' as const,
    }),
    [getWidth]
  );

  const beginResize = useCallback(
    (columnId: string, e: ReactMouseEvent | ReactTouchEvent) => {
      const clientX =
        'touches' in e && e.touches.length > 0 ? e.touches[0].clientX : (e as ReactMouseEvent).clientX;
      e.preventDefault();
      e.stopPropagation();
      const startW = getWidth(columnId);
      dragRef.current = { id: columnId, startX: clientX, startW };
      setResizingColumnId(columnId);

      const onMove = (ev: globalThis.MouseEvent | globalThis.TouchEvent) => {
        const d = dragRef.current;
        if (!d) return;
        const x =
          ev instanceof TouchEvent
            ? ev.touches.length > 0
              ? ev.touches[0].clientX
              : ev.changedTouches[0]?.clientX ?? d.startX
            : ev.clientX;
        const delta = x - d.startX;
        const w = Math.min(maxW, Math.max(minW, d.startW + delta));
        setOverrides((prev) => ({ ...prev, [d.id]: w }));
      };

      const onUp = () => {
        dragRef.current = null;
        setResizingColumnId(null);
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        window.removeEventListener('touchmove', onMove);
        window.removeEventListener('touchend', onUp);
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      window.addEventListener('touchmove', onMove, { passive: false });
      window.addEventListener('touchend', onUp);
    },
    [getWidth, minW, maxW]
  );

  return useMemo(
    () => ({
      getWidth,
      colWidthSx,
      beginResize,
      resizingColumnId,
      sumVisible: (visibleIds: string[]) => sumVisibleColumnWidthsPx(visibleIds, getWidth),
    }),
    [getWidth, colWidthSx, beginResize, resizingColumnId]
  );
}
